import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React from 'react';

import getConfig from '@/configs/global';
import extractSeoData from '@/helpers/seo';

const {
  publicRuntimeConfig: { BASE_URL },
} = getConfig();

function ReferenceProjectsHead({ seoData, pageAttributes, heroImageUrl }) {
  const { asPath } = useRouter();
  const {
    keywords,
    ogImageUrl,
    twitterTitle,
    twitterDescription,
    twitterImageUrl,
  } = extractSeoData(seoData);

  const [pathname] = asPath.split('?');
  const ogUrl = `${BASE_URL}${pathname}`;
  const imgUrl = ogImageUrl || heroImageUrl;

  return (
    <Head>
      <title>{seoData?.metaTitle || pageAttributes.title}</title>
      <meta
        key="description"
        name="description"
        content={seoData?.metaDescription || pageAttributes.description}
      />
      {keywords && <meta name="keywords" content={keywords}></meta>}
      <meta
        property="og:title"
        content={seoData?.metaTitle || pageAttributes.title}
        key="og:title"
      />
      <meta
        property="og:description"
        content={seoData?.metaDescription || pageAttributes.description}
        key="og:description"
      />
      {imgUrl && (
        <>
          <meta property="og:image" content={imgUrl} key="og:image" />
          <meta
            property="og:image:secure_url"
            content={imgUrl}
            key="og:image:secure_url"
          />
        </>
      )}
      <meta property="og:url" content={ogUrl}></meta>
      <meta property="og:type" content="website" key="og:type" />
      <meta property="og:locale" content="de_DE" key="og:locale" />
      <meta
        name="twitter:card"
        content="summary_large_image"
        key="twitter:card"
      />
      {twitterTitle && (
        <meta name="twitter:title" content={twitterTitle} key="twitter:title" />
      )}
      {twitterDescription && (
        <meta
          name="twitter:description"
          content={twitterDescription}
          key="twitter:description"
        />
      )}
      {twitterImageUrl && (
        <meta
          name="twitter:image"
          content={twitterImageUrl}
          key="twitter:image"
        />
      )}
      {seoData?.structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(seoData?.structuredData),
          }}
          key="page-jsonld"
        />
      )}
    </Head>
  );
}

ReferenceProjectsHead.propTypes = {
  seoData: PropTypes.object,
  pageAttributes: PropTypes.object,
  heroImageUrl: PropTypes.string,
};

export default ReferenceProjectsHead;
